import { useRef, useState } from "react";
import { ImagePlus } from "lucide-react";
import { toast } from "sonner";
import api, { fmtErr, imgSrc } from "../lib/api";

const EMPTY = {
  title: "", description: "", price: "", beds: 3, baths: 2, sqft: 1000,
  type: "Apartment", listing_type: "sale", locality: "", city: "Gurgaon",
  address: "", project_name: "", images: "", amenities: "",
};

function fromListing(p) {
  return {
    title: p.title, description: p.description || "", price: p.price, beds: p.beds, baths: p.baths, sqft: p.sqft,
    type: p.type, listing_type: p.listing_type, locality: p.locality, city: p.city || "Gurgaon",
    address: p.address || "", project_name: p.project_name || "",
    images: (p.images || []).join("\n"), amenities: (p.amenities || []).join(", "),
  };
}

function ImageUploader({ onUploaded }) {
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const handle = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        onUploaded(data.url);
      }
      toast.success(`${files.length} photo${files.length > 1 ? "s" : ""} uploaded`);
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="mt-2">
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" onChange={handle} data-testid="listing-file-input" />
      <button type="button" className="dash-btn-ghost" disabled={busy} onClick={() => inputRef.current?.click()} data-testid="listing-upload-button">
        <ImagePlus size={13} /> {busy ? "Uploading…" : "Upload from device"}
      </button>
    </div>
  );
}

export default function ListingForm({ initial = null, onDone, onCancel = null, disabled = false, showProject = false }) {
  const [form, setForm] = useState(() => (initial ? fromListing(initial) : EMPTY));
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = {
      ...form,
      price: Number(form.price),
      beds: Number(form.beds),
      baths: Number(form.baths),
      sqft: Number(form.sqft),
      price_unit: form.listing_type === "rent" ? "monthly" : "total",
      images: form.images.split("\n").map((s) => s.trim()).filter(Boolean),
      amenities: form.amenities.split(",").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (initial) {
        await api.patch(`/properties/${initial.id}`, payload);
        toast.success("Listing saved — resubmitted to the sales desk");
      } else {
        await api.post("/properties", payload);
        toast.success("Submitted — goes live once the sales desk approves it");
      }
      onDone?.();
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="dash-card max-w-3xl p-8" data-testid="listing-form-card">
      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-black/45">
        {initial ? "Edit listing — resubmits for review" : "New listing — goes live after the sales desk approves"}
      </p>
      <form onSubmit={submit} className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="listing-form">
        <div className="sm:col-span-2"><label className="dash-label">Title</label><input required className="dash-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="listing-title-input" /></div>
        <div className="sm:col-span-2"><label className="dash-label">Description</label><textarea rows={3} className="dash-input resize-none" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="listing-description-input" /></div>
        <div><label className="dash-label">Price (₹{form.listing_type === "rent" ? "/month" : " total"})</label><input required type="number" min="1" className="dash-input" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="listing-price-input" /></div>
        <div><label className="dash-label">Listing Type</label>
          <select className="dash-input" value={form.listing_type} onChange={(e) => setForm({ ...form, listing_type: e.target.value })} data-testid="listing-type-select">
            <option value="sale">For Sale</option><option value="rent">For Lease</option>
          </select>
        </div>
        <div><label className="dash-label">Property Type</label>
          <select className="dash-input" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="listing-property-type">
            {["Apartment", "Villa", "Penthouse", "Builder Floor", "Plot"].map((t) => <option key={t}>{t}</option>)}
          </select>
        </div>
        <div><label className="dash-label">Locality</label><input required className="dash-input" placeholder="e.g. Golf Course Road" value={form.locality} onChange={(e) => setForm({ ...form, locality: e.target.value })} data-testid="listing-locality-input" /></div>
        <div><label className="dash-label">Beds</label><input type="number" min="0" className="dash-input" value={form.beds} onChange={(e) => setForm({ ...form, beds: e.target.value })} data-testid="listing-beds-input" /></div>
        <div><label className="dash-label">Baths</label><input type="number" min="0" className="dash-input" value={form.baths} onChange={(e) => setForm({ ...form, baths: e.target.value })} data-testid="listing-baths-input" /></div>
        <div><label className="dash-label">Area (ft²)</label><input type="number" min="0" className="dash-input" value={form.sqft} onChange={(e) => setForm({ ...form, sqft: e.target.value })} data-testid="listing-sqft-input" /></div>
        {showProject && (
          <div><label className="dash-label">Project Name</label><input className="dash-input" value={form.project_name} onChange={(e) => setForm({ ...form, project_name: e.target.value })} data-testid="listing-project-input" /></div>
        )}
        <div className="sm:col-span-2">
          <label className="dash-label">Photos — paste URLs (one per line) or upload from device</label>
          <textarea rows={3} className="dash-input resize-none font-mono text-xs" value={form.images} onChange={(e) => setForm({ ...form, images: e.target.value })} data-testid="listing-images-input" />
          <ImageUploader onUploaded={(url) => setForm((f) => ({ ...f, images: f.images ? `${f.images}\n${url}` : url }))} />
          {form.images.trim() && (
            <div className="mt-2 flex flex-wrap gap-2" data-testid="listing-image-previews">
              {form.images.split("\n").map((s) => s.trim()).filter(Boolean).map((u) => (
                <img key={u} src={imgSrc(u)} alt="" className="h-14 w-20 border border-black/15 object-cover" />
              ))}
            </div>
          )}
        </div>
        <div className="sm:col-span-2"><label className="dash-label">Amenities — comma separated</label><input className="dash-input" placeholder="Private Pool, Concierge, Smart Home" value={form.amenities} onChange={(e) => setForm({ ...form, amenities: e.target.value })} data-testid="listing-amenities-input" /></div>
        <div className="flex flex-wrap gap-3 sm:col-span-2">
          <button type="submit" className="dash-btn" disabled={disabled || busy} data-testid="listing-submit-button">
            {busy ? "Saving…" : initial ? "Save & Resubmit" : "Submit for Review"}
          </button>
          <button
            type="button"
            className="dash-btn-ghost"
            data-testid="listing-reset-button"
            onClick={() => { setForm(initial ? fromListing(initial) : EMPTY); toast.success("Form cleared"); }}
          >
            Reset
          </button>
          {onCancel && <button type="button" className="dash-btn-ghost" onClick={onCancel} data-testid="listing-cancel-edit">Cancel</button>}
        </div>
      </form>
    </div>
  );
}
